/**
 * Privacy Policy page — ai-tokens.me (end-user-facing).
 */

import React, { useEffect } from 'react';
import { Shield } from 'lucide-react';

export default function PrivacyPolicyPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="container" style={{ maxWidth: 800, padding: '48px 24px', marginTop: 64 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em' }}>
          <Shield size={22} style={{ verticalAlign: 'text-bottom', marginRight: 8, color: 'var(--accent)' }} />
          Privacy Policy
        </h1>
        <p style={{ color: 'var(--text-dim)', marginTop: 4, fontSize: '0.9rem' }}>Last updated: March 22, 2026</p>
      </div>

      <div style={{ color: 'var(--text)', lineHeight: 1.8, fontSize: '0.95rem' }}>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>1. Introduction</h2>
          <p>CodeTunez Studios ("we", "us", "our") operates AI Tokens, including the website ai-tokens.me and related services (collectively, the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.</p>
          <p style={{ marginTop: 12 }}>By using the Service, you consent to the data practices described in this policy. If you do not agree, please discontinue use of the Service.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>2. Information We Collect</h2>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginTop: 16, marginBottom: 8 }}>2.1 Information You Provide</h3>
          <ul style={{ paddingLeft: 24, marginTop: 8 }}>
            <li>Account registration information (name, email address, password)</li>
            <li>Token purchase history and transaction records</li>
            <li>Communications you send to us (support requests, feedback)</li>
          </ul>

          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginTop: 16, marginBottom: 8 }}>2.2 Information Collected Automatically</h3>
          <ul style={{ paddingLeft: 24, marginTop: 8 }}>
            <li>IP address, browser type, operating system, and device information</li>
            <li>Usage data including which apps you use your tokens with, request timestamps, and token consumption</li>
            <li>Cookies for session management and authentication</li>
          </ul>

          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginTop: 16, marginBottom: 8 }}>2.3 Information We Do Not Collect</h3>
          <ul style={{ paddingLeft: 24, marginTop: 8 }}>
            <li>We do not store the content of your AI conversations — prompts and responses are not retained after processing</li>
            <li>We do not store your raw user tokens — only cryptographic hashes are kept</li>
            <li>We do not collect payment card numbers — payment processing is handled by trusted third-party processors</li>
          </ul>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul style={{ paddingLeft: 24, marginTop: 8 }}>
            <li>Provide, operate, and maintain the Service</li>
            <li>Process your token purchases and manage your balance</li>
            <li>Track your token usage across apps for billing and your transaction history</li>
            <li>Send you important account notifications (purchase confirmations, security alerts, low balance warnings)</li>
            <li>Prevent fraud and abuse</li>
            <li>Improve the Service</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>4. Data Sharing and Disclosure</h2>
          <p>We do not sell your personal information. We may share your information only in these circumstances:</p>
          <ul style={{ paddingLeft: 24, marginTop: 8 }}>
            <li><strong>Service Providers:</strong> Trusted vendors who help us operate the Service (cloud hosting, payment processing), bound by confidentiality obligations.</li>
            <li><strong>App Developers:</strong> When you use your tokens in a developer's application, that developer may receive limited information such as your anonymised user ID and token usage. Developers do not receive your name, email, or other personal identifiers.</li>
            <li><strong>AI Model Providers:</strong> When you use AI features, your input is sent to the relevant AI provider (e.g., OpenAI, Anthropic) for processing. We do not control how these providers handle your data — please review their privacy policies.</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation.</li>
            <li><strong>Protection of Rights:</strong> When necessary to protect our rights, your safety, or the safety of others.</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
          </ul>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>5. Data Retention</h2>
          <p>We retain your account information for as long as your account is active. Transaction history is retained for up to 90 days for your records and our billing purposes. After you delete your account, your personal information is removed within 30 days.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>6. Data Security</h2>
          <p>We take the security of your data seriously:</p>
          <ul style={{ paddingLeft: 24, marginTop: 8 }}>
            <li>All data is encrypted when sent over the internet (TLS 1.2+)</li>
            <li>All stored data is encrypted at rest</li>
            <li>Your user tokens are stored as cryptographic hashes — the raw values cannot be recovered</li>
            <li>Passwords are securely hashed and never stored in plain text</li>
          </ul>
          <p style={{ marginTop: 12 }}>While we use industry-standard security measures, no system is perfectly secure. We cannot guarantee absolute security of your data.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>7. Your Rights</h2>
          <p>Depending on where you live, you may have the right to:</p>
          <ul style={{ paddingLeft: 24, marginTop: 8 }}>
            <li>See what personal information we have about you</li>
            <li>Correct inaccurate information</li>
            <li>Delete your account and personal data</li>
            <li>Download your data in a portable format</li>
            <li>Object to certain uses of your data</li>
          </ul>
          <p style={{ marginTop: 12 }}>To exercise any of these rights, email us at <a href="mailto:privacy@codetunezstudios.com" style={{ color: 'var(--accent)' }}>privacy@codetunezstudios.com</a>. We will respond within 30 days.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>8. International Data Transfers</h2>
          <p>Our Service runs on Amazon Web Services (AWS). Your data may be processed in the United States or other countries where AWS operates. By using the Service, you agree to your data being transferred to these locations, which may have different data protection laws than your home country.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>9. Children's Privacy</h2>
          <p>The Service is not intended for anyone under the age of 16. We do not knowingly collect information from children. If we learn that we have collected information from a child under 16, we will promptly delete it.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>10. Cookies</h2>
          <p>We use essential cookies to keep you logged in and to manage your session. We do not use advertising or third-party tracking cookies.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>11. Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. When we make material changes, we will update the "Last updated" date at the top of this page. Your continued use of the Service after changes are posted means you accept the updated policy.</p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>12. Contact Us</h2>
          <p>If you have questions about this Privacy Policy:</p>
          <p style={{ marginTop: 8 }}>
            CodeTunez Studios<br />
            Email: <a href="mailto:privacy@codetunezstudios.com" style={{ color: 'var(--accent)' }}>privacy@codetunezstudios.com</a>
          </p>
        </section>

      </div>
    </div>
  );
}
